import type { GridCell, GridCoord, ProgressionState, Vehicle } from '../../types/core';
import { coordToWorld } from '../../utils/grid';
import { vehicleId } from '../../utils/id';
import { pick } from '../../utils/random';

function roadRoute(cells: readonly GridCell[], size: number, rng: () => number): GridCoord[] {
  const roads = cells.filter((cell) => cell.road).map((cell) => cell.coord);
  if (roads.length < 4) {
    return [];
  }

  const start = pick(roads, rng);
  const sorted = [...roads].sort((a, b) => {
    const angleA = Math.atan2(a.z - start.z, a.x - start.x);
    const angleB = Math.atan2(b.z - start.z, b.x - start.x);
    return angleA - angleB;
  });

  return sorted.slice(0, Math.min(sorted.length, Math.max(8, Math.floor(size * 1.25))));
}

function targetVehicleCount(population: number, roadCount: number, hasBus: boolean): number {
  const cars = Math.min(80, Math.floor(population / 4) + Math.floor(roadCount / 32));
  return hasBus ? cars + 2 : cars;
}

export function updateVehicles(
  vehicles: readonly Vehicle[],
  cells: readonly GridCell[],
  size: number,
  population: number,
  progression: ProgressionState,
  deltaMinutes: number,
  rng: () => number
): readonly Vehicle[] {
  const roads = cells.filter((cell) => cell.road);
  const hasBus = progression.unlockedServices.includes('bus');
  const targetCount = targetVehicleCount(population, roads.length, hasBus);
  const created: Vehicle[] = [...vehicles];

  while (created.length < targetCount && roads.length > 0) {
    const route = roadRoute(cells, size, rng);
    if (route.length === 0) {
      break;
    }

    const kind = hasBus && created.filter((vehicle) => vehicle.kind === 'bus').length < 2 ? 'bus' : rng() > 0.82 ? 'delivery' : 'car';
    const first = route[0] ?? { x: 0, z: 0 };
    created.push({
      id: vehicleId(`${kind}-${created.length}-${Math.floor(rng() * 100000)}`),
      kind,
      route,
      routeIndex: 0,
      position: coordToWorld(first, size),
      speed: kind === 'bus' ? 0.11 : kind === 'delivery' ? 0.14 : 0.17,
      service: kind === 'bus'
    });
  }

  return created.slice(0, targetCount).map((vehicle) => {
    if (vehicle.route.length === 0) {
      return vehicle;
    }

    const targetCoord = vehicle.route[vehicle.routeIndex] ?? vehicle.route[0];
    const target = coordToWorld(targetCoord, size);
    const step = vehicle.speed * deltaMinutes;
    const dx = target.x - vehicle.position.x;
    const dz = target.z - vehicle.position.z;
    const distance = Math.hypot(dx, dz);
    const reached = distance < 0.03 || step >= distance;

    return {
      ...vehicle,
      position: reached
        ? target
        : {
            x: vehicle.position.x + (dx / Math.max(distance, 0.001)) * step,
            z: vehicle.position.z + (dz / Math.max(distance, 0.001)) * step
          },
      routeIndex: reached ? (vehicle.routeIndex + 1) % vehicle.route.length : vehicle.routeIndex
    };
  });
}
